import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ShieldCheck, Plus } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Field, FormRow, fmtDate } from './design';

// The CHSP privacy notification and the two DEX consents, kept apart as the CHSP Data Exchange Guide
// asks: the notification is given before the My Aged Care ID is used (it is not a consent); consent to
// DSS storing their name and street address; consent to future contact for research. Each is dated
// with how it was given; a consent can be withdrawn, and the next DEX update sends it as no.

export const CONSENT_KINDS = {
  privacy_notice: 'CHSP privacy notification given',
  provide_details: 'Consents to DSS storing their name and street address',
  future_contact: 'Consents to future contact for research',
};
// What withdrawing each consent changes, for the confirmation.
const WITHDRAW_EFFECT = {
  provide_details: 'From today their name and street address are no longer sent to DEX, and the next update tells DEX the consent is withdrawn.',
  future_contact: 'From today they are no longer available for research contact, and the next DEX update says so.',
};
const METHODS = [['in_person', 'In person'], ['phone', 'Phone'], ['email', 'Email'], ['post', 'Post'], ['form', 'Signed form'], ['other', 'Other']];
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export default function DexConsents({ clientId, onChanged }) {
  const confirm = useConfirm();
  const [data, setData] = useState({ records: [], state: null });
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  // A failed load must not read as "no privacy notification recorded": it says it failed.
  const load = useCallback(() => {
    setError(null);
    api.get('/aged-care/dex/consents', { params: { client_id: clientId } }).then((r) => setData(r.data || { records: [] }))
      .catch((e) => { setData({ records: [], state: null }); setError(e.response?.data?.message || 'Could not load the privacy notification and consents'); });
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      await api.post('/aged-care/dex/consents', { clientId, ...form });
      toast.success('Recorded'); setForm(null); load(); if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  // Asks first: a withdrawal changes what DEX is told about the person from today, and the record
  // keeps it (a consent given again is recorded as a new one).
  const withdraw = async (r) => {
    const ok = await confirm(WITHDRAW_EFFECT[r.kind] || 'From today the consent is no longer in force, and the next DEX update says so.',
      { title: 'Mark this consent as withdrawn?', confirmLabel: 'Mark as withdrawn', danger: true });
    if (!ok) return;
    try {
      await api.post(`/aged-care/dex/consents/${r.id}/withdraw`, { withdrawnOn: isoToday() });
      toast.success('Withdrawn from today'); load(); if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const st = data.state || {};
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 text-[12.5px]">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-slate-800 flex items-center gap-1.5"><ShieldCheck size={14} /> Privacy notification and consents</p>
        {!form && !error && <button type="button" className="btn-ghost btn-sm" onClick={() => setForm({ kind: st.privacyNotice ? 'provide_details' : 'privacy_notice', givenOn: isoToday(), method: 'in_person', version: 'CHSP Standard Privacy Notification' })}><Plus size={13} /> Record</button>}
      </div>
      {error ? (
        <p role="alert" className="text-red-800">{error} <button type="button" className="underline ml-1" onClick={load}>Try again</button></p>
      ) : (
        <p className={st.privacyNotice ? 'text-slate-600' : 'text-amber-800'}>
          {st.privacyNotice ? `Privacy notification given ${fmtDate(st.privacyNotice.on)}${st.privacyNotice.version ? ` (${st.privacyNotice.version})` : ''}.` : 'No privacy notification recorded: give it before their My Aged Care ID is used.'}
          {' '}Name and street address: <span className="font-medium">{st.provideDetails ? 'consented, sent to DEX' : 'not consented, never sent'}</span>.
          {' '}Research contact: <span className="font-medium">{st.futureContact ? 'consented' : 'not consented'}</span>.
        </p>
      )}
      {(data.records || []).length > 0 && (
        <ul className="divide-y divide-slate-100">
          {data.records.map((r) => (
            <li key={r.id} className="py-1 flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-slate-700">
                {CONSENT_KINDS[r.kind] || 'Consent'} · {fmtDate(r.given_on)} · {(METHODS.find(([k]) => k === r.method) || [null, 'Other'])[1]}
                {r.version ? ` · ${r.version}` : ''}{r.recorded_by_name ? ` · recorded by ${r.recorded_by_name}` : ''}
                {r.withdrawn_on && <span className="text-red-800"> · withdrawn {fmtDate(r.withdrawn_on)}</span>}
              </span>
              {r.kind !== 'privacy_notice' && !r.withdrawn_on && <button type="button" className="btn-ghost btn-sm" onClick={() => withdraw(r)}>Mark as withdrawn</button>}
            </li>
          ))}
        </ul>
      )}
      {form && (
        <div className="rounded-lg border border-slate-200 p-2.5 space-y-2">
          <Field label="What">
            {(id) => (
              <select id={id} className="input-field" value={form.kind} onChange={set('kind')}>
                {Object.entries(CONSENT_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            )}
          </Field>
          <FormRow cols={2}>
            <Field label="Given on">{(id) => <input id={id} type="date" className="input-field" value={form.givenOn} max={isoToday()} onChange={set('givenOn')} />}</Field>
            <Field label="How">
              {(id) => <select id={id} className="input-field" value={form.method} onChange={set('method')}>{METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
            </Field>
          </FormRow>
          {form.kind === 'privacy_notice' && (
            <Field label="Version of the notification">{(id) => <input id={id} className="input-field" value={form.version} onChange={set('version')} />}</Field>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost btn-sm" onClick={() => setForm(null)}>Cancel</button>
            <button type="button" className="btn-primary btn-sm" onClick={save}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
